// Demo guide content: one storyline in the order of a working day. "Start
// here" shows how Ace works on the Intake Queue; then one tour per ICT
// procedure (docs/Proxy - RDQ and Material Management Procedures), each
// following one real piece of work start to finish. Every page in the Proxy
// sidebar is introduced inside the first procedure that uses it, and each
// procedure's other cases sit on an "Other cases" step.
// Copy describes the kind of case, never a particular mock company.
//
// Copy is shown as-is in the guide: plain sentences, no em dashes.
//
// `target` is a CSS selector: a `[data-tour="…"]` hook or a sidebar link.
// `setup` puts the app in the right state first (opens a task, an email…).

export type TourSetup =
  | { kind: 'openTask'; taskId: string }
  | { kind: 'requestFollowUp'; jobId: string }
  // Opens the notification bell (the guide closes it again on the next step).
  | { kind: 'openNotifications' };

// One of a procedure's other cases, opened from the "Other cases" step.
export interface TourCase {
  label: string;
  note: string;
  route?: string;
  setup?: TourSetup;
}

export interface TourStep {
  title: string;
  today?: string;   // how the team does it now; left out when there's no manual equivalent
  withAce: string;
  route?: string;
  // A list means: spotlight the first one that's on screen.
  target?: string | string[];
  setup?: TourSetup;
  cases?: TourCase[];
}

export interface Tour {
  id: string;
  label: string;
  title: string;
  summary: string;
  steps: TourStep[];
}

const t = (hook: string) => `[data-tour="${hook}"]`;
const email = (id: string) => `/email-inbox?emailId=${id}&open=1`;

// Plain-English meanings for the terms the tours use, for someone new to
// proxy distribution.
export const GLOSSARY: { term: string; meaning: string }[] = [
  { term: 'Ace', meaning: 'This app, and the AI assistant inside it. Ace reads the inbox and the reports, follows the ICT procedures and drafts every email and MIC entry. A person checks and approves each one.' },
  { term: 'Proxy material', meaning: 'What shareholders receive before a company\'s shareholder meeting: the proxy statement, a proxy card or voting form, and sometimes the annual report. BetaNXT (Mediant) mails it out on behalf of brokers.' },
  { term: 'ICT / Collections', meaning: 'The team that collects proxy material from solicitors and issuers in time to mail it. The procedures in this guide (ICT-1, ICT-2 and so on) are theirs.' },
  { term: 'Task', meaning: 'One thing Ace has prepared for you to check and approve, like a follow-up email or an RDQ order. Tasks wait in the Intake Queue.' },
  { term: 'Ticket', meaning: 'One piece of work from start to finish. A shortage, a chase or a contest can take several tasks; the ticket closes when the last one is approved, and then shows in Completed.' },
  { term: 'Exception', meaning: 'A task Ace can\'t finish without a person, such as a job with nobody to contact.' },
  { term: 'Escalation', meaning: 'A task waiting on the ICT manager, either because the procedure says so or because someone asked.' },
  { term: 'Band', meaning: 'How close a job\'s meeting is: Red, Yellow or Green.' },
  { term: 'Issuer', meaning: 'The company holding the meeting.' },
  { term: 'Solicitor', meaning: 'The firm the issuer hires to send out its material, such as Georgeson, D.F. King or Broadridge. ICT chases them for it.' },
  { term: 'Job / Job #', meaning: 'One meeting\'s distribution, tracked under a job number.' },
  { term: 'CUSIP', meaning: 'The nine-character ID of a security. It\'s how a job is looked up on EDGAR, SEDAR+ or Broadridge\'s RICS.' },
  { term: 'Meeting date and vote cutoff', meaning: 'The meeting date decides whether a job is Red or Yellow. The vote cutoff, just before it, sets the last days for posting material, emailing it, or aborting.' },
  { term: 'Red, Yellow, Green', meaning: 'How close the meeting is. Red is 1 to 18 days away (urgent, ICT-8), Yellow is 19 to 30 (chase, ICT-1), Green is more than 30 (just monitor).' },
  { term: 'Full set', meaning: 'One complete printed package of the material, for one holder.' },
  { term: 'NAA', meaning: 'Notice and Access. A short notice telling holders where to find the material online. Only ordered from the solicitor on Canadian jobs.' },
  { term: 'E-consent holders', meaning: 'Holders who agreed to get their material by email, so they don\'t need a printed set.' },
  { term: 'RDQ', meaning: 'Record Date Quantity. How many sets to order from the solicitor, worked out from holder counts on the record date (ICT-6).' },
  { term: 'VIF, BRE, SMRF', meaning: 'Voting instruction form, business reply envelope and supplemental mailing request form. VIFs are made in-house and SMRFs never hold a job up, so neither is chased.' },
  { term: 'POD', meaning: 'Print on demand, where BetaNXT prints the material itself. It\'s free up to 100 pages and 50 full-set holders (or 50 shorted sets on a shortage). Anything bigger needs the manager.' },
  { term: 'MIC', meaning: 'The job system where ICT checks what material has arrived and records every follow-up.' },
  { term: 'BOC', meaning: 'The system that holds job setup, billing and the Broker Search Card. Discovery keeps it up to date.' },
  { term: 'Broker Search Card (BSC)', meaning: 'The part of the job that says who the solicitor is and, for Canada, the intermediary mail date.' },
  { term: 'Discovery (documents@)', meaning: 'The team that sets up jobs and changes their dates, mailing method or status, aborts included.' },
  { term: 'Verifications (verifications@)', meaning: 'The team that checks material PDFs and releases the emails to e-consent holders.' },
  { term: 'Fulfillment Center (proxymail@)', meaning: 'The team that receives the physical material and mails it.' },
  { term: 'EDGAR and SEDAR+', meaning: 'The public US and Canadian filing sites. If the proxy statement has been filed there, ICT can pull it instead of waiting on the solicitor.' },
  { term: 'RICS', meaning: 'Broadridge\'s portal. For Broadridge jobs ICT checks the status there instead of sending an email.' },
  { term: 'Proxy contest', meaning: 'When activist investors (the dissident party) oppose a proposal. ICT needs their signed contest form and their billing details (ICT-3).' },
  { term: 'Contact Tracing', meaning: 'Research to find someone to contact when a job has no solicitor and nobody has been chased before.' },
  { term: 'Alteryx', meaning: 'The tool that normally builds the daily follow-up report. ICT-7 covers what to do when it\'s down.' },
];

export const DEMO_TOURS: Tour[] = [
  {
    id: 'start',
    label: 'Start here',
    title: 'How Ace works',
    summary: 'Ace reads everything that comes in, works out what the ICT procedure says to do, and gets it ready. You check it and approve it. This tour shows that on the Intake Queue, the team\'s main page.',
    steps: [
      {
        title: 'Ace prepares, you approve',
        today: 'Someone reads every email and report, looks each job up in MIC and works out what to do.',
        withAce: 'Every email, report and notification that needs doing is already a task here. The line at the top says how much Ace read today and how many tasks it made.',
        route: '/proxy', target: t('intake-summary'),
      },
      {
        title: 'Find what you need',
        withAce: 'Filter by band, which is how close the meeting is (Red is 18 days or less), or by the kind of work. Click any row to open it.',
        route: '/proxy', target: t('intake-filters'),
      },
      {
        title: 'Every task says why it exists',
        withAce: 'This one is a solicitor\'s Yellow follow-up. They have several jobs on today\'s report, so Ace put them all in one email, as ICT-1 says to.',
        setup: { kind: 'openTask', taskId: 'material-followup-yellow-fu-american-d-f-king' }, target: t('task-why'),
      },
      {
        title: 'Check what Ace found',
        withAce: 'These are the details Ace pulled from the email, the report and MIC. If one is wrong, click Edit and change it. The rest of the task updates to match.',
        target: t('task-extracted'),
      },
      {
        title: 'Check the email',
        withAce: 'The email is written and addressed. Read it, and change anything you like before it goes.',
        target: t('task-drafts'),
      },
      {
        title: 'Approve it',
        withAce: 'Nothing is sent until you approve. Approving sends the email, records it in MIC and takes the task off the queue. Not sure? Escalate it to the manager.',
        target: t('task-actions'),
      },
      {
        title: 'Where to go next',
        withAce: 'The tours that follow are the ICT procedures, in the order of a working day. Each page in the sidebar comes up in the first procedure that uses it. Click Next to carry on, and if a word is new, open the Glossary.',
      },
    ],
  },
  {
    id: 'ICT-4',
    label: 'ICT-4 Inbox',
    title: 'Sorting the ICT inbox',
    summary: 'Every email that arrives in the ICT inbox gets read, sent to the right desk and filed.',
    steps: [
      {
        title: 'The Email Inbox',
        withAce: 'This is mail to and from outside parties: solicitors, issuers, Discovery and Verifications. The Proxy and Fulfillment teams don\'t email each other; they use the bell, which comes up in the shortage tour.',
        route: '/email-inbox', target: t('inbox-mailboxes'),
      },
      {
        title: 'Every email gets a label',
        today: 'Open Outlook and read each email in the ICT inbox to work out what it is.',
        withAce: 'Ace reads mail as it lands and labels it: material PDF, job change, RDQ, contest, credit hold, report, reply, bounce. If Ace got one wrong, you can change it.',
        route: '/email-inbox', target: t('email-category'),
      },
      {
        title: 'Open one and Ace says where it goes',
        today: 'Date changes go to documents@, verifications@ and notifications@ unless they\'re already copied, then into Miscellaneous Completed.',
        withAce: 'The top says what Ace made of it: which desks it goes to and which folder it\'s filed in. Below is the whole conversation.',
        route: email('pinbox-009'), target: t('email-ace-read'),
      },
      {
        title: 'It\'s already a task',
        withAce: 'Anything that needs doing becomes a task. Click the link to open it.',
        route: email('pinbox-009'), target: t('drawer-task-link'),
      },
      {
        title: 'Approve the routing',
        withAce: 'Approving forwards it and files it. For a date change, Ace also holds the job\'s chases until Discovery has the new dates.',
        setup: { kind: 'openTask', taskId: 'route-job_change_route-pinbox-009' }, target: t('task-approve-plan'),
      },
      {
        title: 'Reports get deleted',
        today: 'Delete incoming reports if the ICT manager is in. If they\'re out, forward the reports to the team first, then delete them.',
        withAce: 'Ace knows a scheduled report. When the manager is marked out of office on the Admin Dashboard, Ace forwards it to the team before deleting it.',
        route: email('pinbox-034'), target: t('drawer-manager-out'),
      },
      {
        title: 'Other cases',
        withAce: 'Open one to see where Ace sends it.',
        cases: [
          { label: 'Material PDF', note: 'If all three desks are already copied, it\'s just filed.', route: email('pinbox-001') },
          { label: 'Credit hold', note: 'Goes to the credit associate, and Ace notes the hold on the job.', route: email('pinbox-006') },
          { label: 'Billing verification', note: 'Discovery\'s request goes to the billing associate.', route: email('pinbox-003') },
          { label: 'Revised RDQ', note: 'Goes to the RDQ associate. See ICT-6.', route: email('pinbox-004') },
          { label: 'Proxy contest', note: 'Goes to the contest associate. See ICT-3.', route: email('pinbox-005') },
          { label: 'Red Report', note: 'Filed in Red Reports. Nothing to approve.', route: email('pinbox-007') },
          { label: 'A bounce', note: 'Goes to Exceptions so someone confirms a new contact.', route: email('pinbox-042') },
          { label: 'Nothing fits', note: 'Ace says no rule matched and leaves it for you.', route: email('pinbox-036') },
        ],
      },
    ],
  },
  {
    id: 'ICT-2',
    label: 'ICT-2 Audit',
    title: 'Checking the Proxy Material Inbox',
    summary: 'A daily check that nothing in the Proxy Material Inbox slipped past the people who need to act on it.',
    steps: [
      {
        title: 'Only the emails nobody saw',
        today: 'Every day, check that each of today\'s emails in the Proxy Material Inbox reached someone on the ICT team, documents@ or verifications@.',
        withAce: 'Ace does that check and lists only today\'s emails that didn\'t reach anyone who acts on them.',
        route: '/email-inbox?mailbox=audit', target: t('audit-banner'),
      },
      {
        title: 'Ace suggests where each one goes',
        today: 'Job setup changes go to documents@, PDFs to documents@ and verifications@, and anything else to the manager.',
        withAce: 'Ace follows those rules. If you disagree, change where it\'s going before you forward it.',
        route: '/email-inbox?mailbox=audit', target: t('audit-call'),
      },
      {
        title: 'Forward it and it\'s done',
        today: 'Forward by hand and try to remember which ones are finished.',
        withAce: 'A PDF that reached nobody goes to documents@ and verifications@. Once forwarded, it drops off the list.',
        route: '/email-inbox?mailbox=audit', target: t('email-row-pinbox-044'),
      },
      {
        title: 'Mark as read, never delete',
        today: 'Mark today\'s mail as read. Don\'t delete anything, because this mailbox is the permanent record.',
        withAce: 'One click marks today\'s mail as read. Nothing gets deleted.',
        route: '/email-inbox?mailbox=audit', target: t('mailbox-audit'),
      },
    ],
  },
  {
    id: 'ICT-1',
    label: 'ICT-1 Yellow',
    title: 'Yellow follow-ups',
    summary: 'Chasing solicitors and issuers for material when the meeting is 19 to 30 days away.',
    steps: [
      {
        title: 'The report comes in',
        today: 'Run the Follow Ups job in Alteryx, combine the Red and Yellow tabs, save the file and share it with the team.',
        withAce: 'Alteryx emails the Red and Yellow report to the ICT inbox each day. Ace reads it and gets a task ready for every row.',
        route: email('proxy-report-followups'), target: t('email-ace-read'),
      },
      {
        title: 'Reports: where every job stands',
        withAce: 'The Reports page holds the same report: every job still owed material, split by how close the meeting is. It\'s for looking. The work itself is in the Intake Queue.',
        route: '/proxy/reports', target: t('report-outstanding-yellow'),
      },
      {
        title: 'MIC is already checked',
        today: 'Open every job in MIC, read the Special Instructions, and delete the rows where material has arrived.',
        withAce: 'Jobs that are received, marked do not follow up, or e-consent only never reach the report. Every row left still needs material.',
        route: '/proxy/reports/outstanding-yellow', target: t('report-row-9100004'),
      },
      {
        title: 'One email per solicitor',
        today: 'Work the report one solicitor at a time, picking the US, Canadian, Broadridge or Issuer template.',
        withAce: 'When a solicitor has several jobs on the report, Ace sends them one email with every job in the grid, using the template that fits (here FU-American).',
        setup: { kind: 'requestFollowUp', jobId: '9100004' }, target: t('task-decision'),
      },
      {
        title: 'Approve, and MIC is updated',
        today: 'In MIC, add a Meeting Reminder follow-up on each job, one at a time.',
        withAce: 'Approving sends the email and logs the follow-up on every job in it. If a job never had an RDQ, Ace adds its New Material line first.',
        target: t('task-approve-plan'),
      },
      {
        title: 'Follow Ups: the next chase is dated',
        today: 'Highlight the row and remember to chase again.',
        withAce: 'Every chase that\'s out is on the Follow Ups page, with the date the next one is due, 4 days later. Overdue ones come first. Click Follow up and Ace writes the next one.',
        route: '/proxy/follow-ups', target: [t('followup-row-9100004:material'), t('followups-counts')],
      },
      {
        title: 'Follow Ups shows the answers too',
        withAce: 'When a solicitor answers, the chase shows Replied, and Open reply takes you to Ace\'s read of it. Bounced means the last chase didn\'t arrive. On hold means Discovery is changing the job\'s dates.',
        route: '/proxy/follow-ups', target: [t('followup-row-9100121:material'), t('followups-counts')],
      },
      {
        title: 'Exceptions: when Ace can\'t go on',
        withAce: 'If a job has nobody to contact, Ace doesn\'t guess an address. It puts the job in Exceptions and asks the manager for Contact Tracing. A chase that bounced lands here too. Clear these first, since nothing goes out on those jobs until you do.',
        route: '/proxy/exceptions', target: [t('task-row-missing-contact-9100031'), t('task-table')],
      },
      {
        title: 'Other cases',
        withAce: 'ICT-1 has a rule for each of these. Open one to see what Ace does.',
        cases: [
          { label: 'A Canadian job', note: 'Canadian jobs get FU-Canadian, asking for full sets and NAA.', setup: { kind: 'requestFollowUp', jobId: '9100005' } },
          { label: 'Canadian mail date still to come', note: 'If the intermediary mail date hasn\'t passed, Ace holds off and notes the date in MIC.', setup: { kind: 'requestFollowUp', jobId: '9100060' } },
          { label: 'Broadridge: check RICS', note: 'Ace reads the RICS status instead of emailing. Material Mailed is just written in MIC.', setup: { kind: 'requestFollowUp', jobId: '9100007' } },
          { label: 'Broadridge: Project Closed', note: 'If RICS shows Project Closed, Ace asks Broadridge why.', setup: { kind: 'requestFollowUp', jobId: '9100008' } },
          { label: 'No solicitor: chase the issuer', note: 'The chase goes to whoever was contacted last time, on FU-Issuer.', setup: { kind: 'requestFollowUp', jobId: '9100003' } },
          { label: 'No solicitor: try last year\'s', note: 'If nobody has been chased yet, Ace asks the solicitor from last year\'s job if they\'re handling it.', setup: { kind: 'requestFollowUp', jobId: '9100009' } },
          { label: 'They said yes', note: 'When last year\'s solicitor confirms, Ace asks Discovery to update the Broker Search Card.', setup: { kind: 'openTask', taskId: 'reply-pinbox-050' } },
          { label: 'A follow-up mailing', note: 'Ace chases the material MIC says is being mailed, not the proxy statement.', setup: { kind: 'requestFollowUp', jobId: '9100015' } },
          { label: 'VIF/BRE only', note: 'If the proxy card PDF is on file there\'s no chase, just the MIC note. If not, Ace asks for it.', setup: { kind: 'requestFollowUp', jobId: '9100110' } },
          { label: 'Reply: "we sent it"', note: 'If MIC hasn\'t logged it, Ace asks the Fulfillment Center to find it by tracking number.', setup: { kind: 'openTask', taskId: 'reply-pinbox-046' } },
          { label: 'Reply: no tracking number', note: 'If there\'s no tracking number, Ace replies and asks for one.', setup: { kind: 'openTask', taskId: 'reply-pinbox-047' } },
          { label: 'Reply: issuer opted out', note: 'A Canadian issuer that opted out of US distribution gets an abort request to Discovery.', setup: { kind: 'openTask', taskId: 'reply-pinbox-049' } },
        ],
      },
    ],
  },
  {
    id: 'ICT-8',
    label: 'ICT-8 Red',
    title: 'Red follow-ups',
    summary: 'Jobs 1 to 18 days from the meeting, worked against the vote cutoff deadlines.',
    steps: [
      {
        title: 'The deadline rules',
        today: 'Work the Red Report against the days left to vote cutoff, keeping every deadline in your head.',
        withAce: 'These are the ICT-8 deadlines Ace applies to every Red job.',
        route: '/proxy/reports/outstanding-all', target: t('board-rules'),
      },
      {
        title: 'Check EDGAR or SEDAR first',
        today: 'At 18 to 11 days, search SEC EDGAR or SEDAR+ by CUSIP, download the filing and count the pages.',
        withAce: 'Ace searches by CUSIP and shows the filing it found, with the page count. If it\'s the wrong filing, use Edit to mark it not found.',
        setup: { kind: 'requestFollowUp', jobId: '9100022' }, target: t('task-decision'),
      },
      {
        title: 'Found it, so release emails only',
        today: 'Send the PDF to Verifications and submit the Everything Else Form to release emails only.',
        withAce: 'Approving does both. The job stays open, since full sets could still arrive.',
        target: t('task-approve-plan'),
      },
      {
        title: 'Escalations: the manager\'s call',
        today: 'Someone has to remember to ask the manager about POD over the limit, or cancelling full sets 10 to 8 days out.',
        withAce: 'When the procedure says the manager decides, Ace sends the task to Escalations with the reason. Anyone can escalate a task too, with an optional comment. Approving it clears it.',
        route: '/proxy/escalations', target: [t('task-row-material-followup-9100033'), t('task-table')],
      },
      {
        title: 'The reason is on the task',
        withAce: 'Open it and the reason sits at the top, so the manager knows exactly what they\'re deciding.',
        setup: { kind: 'openTask', taskId: 'material-followup-9100033' }, target: t('task-escalation'),
      },
      {
        title: 'Send the Red Report at close',
        today: 'Fill in each job\'s status, save the file as "ICT Red Report" with the date, and email it to the manager, documents@, verifications@ and ict@.',
        withAce: 'Ace fills in each job\'s status from what was done today. Have a look and click Send.',
        route: '/proxy/reports/outstanding-red', target: t('red-report-send'),
      },
      {
        title: 'Other cases',
        withAce: 'Open one to see what Ace does.',
        cases: [
          { label: 'No filing found', note: 'Ace writes the Red follow-up to the solicitor.', setup: { kind: 'requestFollowUp', jobId: '9100020' } },
          { label: '10 days: print on demand', note: 'Within the POD limits, which stay internal. The email asks the solicitor to send or pay.', setup: { kind: 'requestFollowUp', jobId: '9100030' } },
          { label: 'Too big for free POD', note: 'Over the limits, it\'s the manager\'s call.', setup: { kind: 'requestFollowUp', jobId: '9100033' } },
          { label: '10 to 5 days: email only', note: 'Between 10 and 8 days the manager decides before full sets are cancelled.', setup: { kind: 'requestFollowUp', jobId: '9100041' } },
          { label: 'Under 5 days: abort', note: 'Ace prepares the Abort Jobs form, and a person approves it.', setup: { kind: 'requestFollowUp', jobId: '9100050' } },
          { label: 'Reply: the meeting moved', note: 'Ace forwards it to Discovery and holds the chase.', setup: { kind: 'openTask', taskId: 'reply-pinbox-048' } },
        ],
      },
    ],
  },
  {
    id: 'ICT-13',
    label: 'ICT-13 Shortage',
    title: 'Chasing a shortage',
    summary: 'When material arrives short, ICT chases the solicitor for the rest until it arrives or they say it isn\'t needed.',
    steps: [
      {
        title: 'Fulfillment logs a short delivery',
        today: 'Run the Proxy Material Received report, filter it to shortages, and share the file with the team.',
        withAce: 'Fulfillment has marked this delivery Received Short and escalated it to Proxy. Ace tells Proxy straight away, in the bell rather than by email, so nobody has to spot it in a report.',
        route: '/escalations', target: t('exception-row-S-P10'),
      },
      {
        title: 'Notifications: how the teams talk',
        withAce: 'Proxy hears about it in the bell, not by email. Both teams use the bell to keep each other posted. Click any notification and you go straight to its row, highlighted.',
        setup: { kind: 'openNotifications' }, target: t('notif-panel'),
      },
      {
        title: 'It\'s a task in the Intake Queue',
        withAce: 'Ace has turned that notification into a shortage follow-up in the Intake Queue, ready for ICT to check.',
        route: '/proxy', target: t('task-row-shortage-3060010'),
      },
      {
        title: 'Ace pulls the details together',
        today: 'Find the CUSIP in BOC, then what was ordered, received and missing in MIC.',
        withAce: 'Ace fills in the CUSIP, the solicitor and each short line. VIFs and SMRFs are left out, since those aren\'t chased.',
        setup: { kind: 'openTask', taskId: 'shortage-3060010' }, target: t('task-extracted'),
      },
      {
        title: 'The chase goes to the solicitor',
        today: 'Fill in the Material Shortage template and send it to the solicitor on the Broker Search Card.',
        withAce: 'The email asks the solicitor to send the rest. The POD limits stay internal, so it asks them to cover the cost if they want it printed.',
        target: t('task-drafts'),
      },
      {
        title: 'Approve, and MIC is updated',
        today: 'Add a Material Short follow-up in MIC with the shorted amount.',
        withAce: 'Approving sends the email, logs the Material Short follow-up, and tells Fulfillment the solicitor has been chased.',
        target: t('task-approve-plan'),
      },
      {
        title: 'Chase again in 4 days',
        today: 'ICT-13 says to chase every day until the material arrives.',
        withAce: 'Once you\'ve approved it, this delivery\'s chase shows in Follow Ups with the next one due 4 days later. The client confirmed the team chases every 4 days, not daily. If you haven\'t approved it yet, go back a step.',
        route: '/proxy/follow-ups', target: [t('followup-row-3060010:shortage'), t('followups-counts')],
      },
      {
        title: 'Completed: the ticket closes',
        withAce: 'When the material arrives, Fulfillment merges it into the original receipt, Ace tells Proxy, and Proxy closes the ticket. Every closed ticket is on the Completed page. Open one to see each step in order: what Ace prepared, who approved it, each email and each message between the teams. Pick any dates at the top.',
        route: '/proxy/completed?open=routing:seed-a01', target: [t('completed-timeline'), t('completed-list')],
      },
      {
        title: 'Other cases',
        withAce: 'When the solicitor answers, Ace reads the reply and keeps Fulfillment posted. When the material arrives, Fulfillment merges it into the original receipt, Ace tells Proxy, and Proxy closes the ticket. It then shows in Completed with its full history.',
        cases: [
          { label: 'It\'s on the way', note: 'Ace tells Fulfillment, with the tracking number, and their receipt waits for the material so they can merge it.', setup: { kind: 'openTask', taskId: 'reply-pinbox-059' } },
          { label: 'They say it isn\'t needed', note: 'Ace tells documents@ and verifications@ and closes the shortage.', setup: { kind: 'openTask', taskId: 'reply-pinbox-058' } },
          { label: 'They ask us to print it', note: 'Within 100 pages and 50 shorted, Ace asks for it to be printed at no cost. Anything bigger goes to the manager.', setup: { kind: 'openTask', taskId: 'reply-pinbox-057' } },
        ],
      },
    ],
  },
  {
    id: 'ICT-6',
    label: 'ICT-6 RDQ',
    title: 'Working out an RDQ',
    summary: 'How many sets to order when Alteryx isn\'t available or an existing order needs changing.',
    steps: [
      {
        title: 'Discovery asks for a revised RDQ',
        today: 'Discovery emails ICT when a job\'s holder counts change.',
        withAce: 'Ace reads the request and links it to the job.',
        route: email('pinbox-004'), target: t('email-ace-read'),
      },
      {
        title: 'The calculator inputs',
        today: 'Open the RDQ calculator spreadsheet next to MIC. Answer "No Elec?" and "Mailing as NAA?", then type in the Active row, not the Test row.',
        withAce: 'Ace fills them in from MIC and ignores the Test row. You can correct any of them with Edit.',
        setup: { kind: 'openTask', taskId: 'rdq-email-pinbox-004-9100022' }, target: t('task-extracted'),
      },
      {
        title: 'The answer',
        today: 'Read the result off the US or CAN tab.',
        withAce: 'US jobs order full sets only. Ace shows how it compares with the order already on file.',
        target: t('task-decision'),
      },
      {
        title: 'The order is ready to send',
        today: 'Update the Materials tab in MIC, then send the order to the solicitor.',
        withAce: 'The order email and the MIC entry are ready. Approving also updates the job\'s order, so later chases ask for the new quantities.',
        target: t('task-drafts'),
      },
      {
        title: 'Other cases',
        withAce: 'Open one to see what Ace does.',
        cases: [
          { label: 'A Canadian job mailing NAA', note: 'Canadian jobs mailing NAA order both full sets and NAA.', setup: { kind: 'openTask', taskId: 'rdq-email-pinbox-017-3025905' } },
          { label: 'Ask Ace directly', note: 'Type "calculate rdq for job" and a job number in Ace.', route: '/ace' },
        ],
      },
    ],
  },
  {
    id: 'ICT-3',
    label: 'ICT-3 Contest',
    title: 'Proxy contests',
    summary: 'Getting a signed contest form and billing details from the dissident party.',
    steps: [
      {
        title: 'Discovery flags a new contest',
        today: 'Discovery tells ICT a proxy contest job has been set up.',
        withAce: 'Ace reads Discovery\'s email and links it to the contest job.',
        route: email('pinbox-005'), target: t('email-ace-read'),
      },
      {
        title: 'The outreach is written',
        today: 'Fill in the Proxy Contest Outreach template, attach the form and send it to the dissident\'s solicitor.',
        withAce: 'Ace writes it to the dissident\'s solicitor with the job, CUSIP, event date, full sets and e-consent holders filled in.',
        setup: { kind: 'openTask', taskId: 'contest-9200001' }, target: t('task-drafts'),
      },
      {
        title: 'Chased until it comes back',
        today: 'Chase three times over a week if the form isn\'t returned.',
        withAce: 'Each contest chase goes into Follow Ups, due every 2 days. A third chase with no answer goes to the manager.',
        route: '/proxy/follow-ups', target: t('followups-counts'),
      },
      {
        title: 'Check who signed',
        today: 'A solicitor\'s signature doesn\'t count. Only the dissident party can sign.',
        withAce: 'When a signed form comes back, set who signed it. If it was the solicitor, Ace sends it back for the dissident to sign.',
        setup: { kind: 'openTask', taskId: 'contest-9200002' }, target: t('task-extracted'),
      },
      {
        title: 'The signed form goes to Discovery',
        today: 'Send the form to Discovery to upload to BOC, then add the billing details.',
        withAce: 'Ace writes the handover to Discovery and records the billing.',
        setup: { kind: 'openTask', taskId: 'contest-9200004' }, target: t('task-drafts'),
      },
      {
        title: 'Other cases',
        withAce: 'Open one to see what Ace does.',
        cases: [
          { label: 'A lower fee', note: 'Any fee under $5,000 goes to the manager to approve.', setup: { kind: 'openTask', taskId: 'contest-9200003' } },
          { label: 'Nobody known to contact', note: 'Ace offers the ICT-3 list of solicitors to try, and you choose.', setup: { kind: 'openTask', taskId: 'contest-9200006' } },
          { label: 'They won\'t send material or pay', note: 'If the dissident won\'t send material or pay, Ace asks Discovery to abort the job.', setup: { kind: 'openTask', taskId: 'contest-9200007' } },
        ],
      },
    ],
  },
  {
    id: 'ICT-7',
    label: 'ICT-7 Manual pull',
    title: 'When Alteryx is down',
    summary: 'Building the follow-up report by hand from Report Server when Alteryx isn\'t working.',
    steps: [
      {
        title: 'Pull the report',
        today: 'Download the Proxy Material Report from Report Server and rebuild the spreadsheet by hand.',
        withAce: 'One click and Ace pulls the report and works through every ICT-7 step.',
        route: '/proxy/reports', target: t('manual-pull-button'),
      },
      {
        title: 'See what each step removed',
        today: 'Delete the Green section, duplicates and New jobs, then read every ops note for 14 exclusion phrases.',
        withAce: 'Ace lists each step and how many jobs it took out, so you can check its work.',
        route: '/proxy/reports/manual-pull', target: t('pull-trace'),
      },
      {
        title: 'Every exclusion has a reason',
        today: 'Skim the ops notes, where a NOBO or Legal note is easy to miss.',
        withAce: 'Each job taken out shows the phrase that removed it. If Ace got one wrong, click Include anyway.',
        route: '/proxy/reports/manual-pull', target: t('pull-step-ops_note'),
      },
      {
        title: 'Split into Red and Yellow',
        today: 'Split the tabs, move jobs 15 to 18 days out into Red, group by solicitor and look up each contact.',
        withAce: 'Already done. Contacts come from the Solicitor Contact List, and missing ones are flagged.',
        route: '/proxy/reports/manual-pull', target: t('pull-tabs'),
      },
      {
        title: 'Send it to the team',
        today: 'Save it as "ICT Follow Ups" with the date and email it round.',
        withAce: 'One click sends it to ict@.',
        route: '/proxy/reports/manual-pull', target: t('pull-send'),
      },
    ],
  },
];
